/*

MIT License

Copyright (c) 2020 react-spring
Copyright (c) 2024 Paragon Component Systems, LLC

Permission is hereby granted, free of charge, to any person obtaining a copy
of this software and associated documentation files (the "Software"), to deal
in the Software without restriction, including without limitation the rights
to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software, and to permit persons to whom the Software is
furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in all
copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
SOFTWARE.

 */

import * as React from 'react';
import { ForwardRefComponent } from '@react-three/drei/helpers/ts-utils';
import { extend, ThreeElement, ThreeElements, useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';

declare module '@react-three/fiber' {
  interface ThreeElements {
    positionMesh: ThreeElement<typeof PositionMesh>;
  }
}

type Api = {
  getParent: () => React.RefObject<THREE.InstancedMesh>;
  subscribe: <T>(ref: React.RefObject<T>) => void;
};

export type InstancesProps = Omit<ThreeElements['instancedMesh'], 'ref' | 'args'> & {
  context?: React.Context<Api>;
  range?: number;
  initialSize?: number;
  sizeIncrement?: number;
  frames?: number;
};

export type InstanceProps = Omit<ThreeElements['positionMesh'], 'ref'> & {
  context?: React.Context<Api>;
};

export type InstancedAttributeProps = Omit<ThreeElements['instancedBufferAttribute'], 'ref' | 'args'> & {
  name: string;
  defaultValue: any;
  normalized?: boolean;
  usage?: number;
};

function isFunctionChild(
  value: any,
): value is (
  props: React.ForwardRefExoticComponent<Omit<InstanceProps, 'ref'> & React.RefAttributes<unknown>>,
) => React.ReactNode {
  return typeof value === 'function';
}

const _instanceLocalMatrix = /* @__PURE__ */ new THREE.Matrix4();
const _instanceWorldMatrix = /* @__PURE__ */ new THREE.Matrix4();
const _instanceIntersects: THREE.Intersection[] = [];
const _mesh = /* @__PURE__ */ new THREE.Mesh<THREE.BufferGeometry, THREE.MeshBasicMaterial>();

export class PositionMesh extends THREE.Group {
  color: THREE.Color;
  instance: React.RefObject<THREE.InstancedMesh | undefined>;
  instanceKey: React.RefObject<PositionMesh | undefined>;
  constructor() {
    super();
    this.color = new THREE.Color('white');
    this.instance = { current: undefined };
    this.instanceKey = { current: undefined };
  }

  // This will allow the virtual instance have bounds
  get geometry() {
    return this.instance.current?.geometry;
  }

  // And this will allow the virtual instance to receive events
  raycast(raycaster: THREE.Raycaster, intersects: THREE.Intersection[]) {
    const parent = this.instance.current;
    if (!parent) return;
    if (!parent.geometry || !parent.material) return;
    _mesh.geometry = parent.geometry;
    const matrixWorld = parent.matrixWorld;
    const instanceId = parent.userData.instances.indexOf(this.instanceKey);
    // If the instance wasn't found or exceeds the parents draw range, bail out
    if (instanceId === -1 || instanceId > parent.count) return;
    // calculate the world matrix for each instance
    parent.getMatrixAt(instanceId, _instanceLocalMatrix);
    _instanceWorldMatrix.multiplyMatrices(matrixWorld, _instanceLocalMatrix);
    // the mesh represents this single instance
    _mesh.matrixWorld = _instanceWorldMatrix;
    // raycast side according to instance material
    if (parent.material instanceof THREE.Material) _mesh.material.side = parent.material.side;
    else _mesh.material.side = parent.material[0].side;
    _mesh.raycast(raycaster, _instanceIntersects);
    // process the result of raycast
    for (let i = 0, l = _instanceIntersects.length; i < l; i++) {
      const intersect = _instanceIntersects[i];
      intersect.instanceId = instanceId;
      intersect.object = this;
      intersects.push(intersect);
    }
    _instanceIntersects.length = 0;
  }
}

const globalContext = /* @__PURE__ */ React.createContext<Api>(null!);
const parentMatrix = /* @__PURE__ */ new THREE.Matrix4();
const instanceMatrix = /* @__PURE__ */ new THREE.Matrix4();

export const Instance = /* @__PURE__ */ React.forwardRef(({ context, children, ...props }: InstanceProps, ref) => {
  React.useMemo(() => extend({ PositionMesh }), []);
  const group = React.useRef<PositionMesh>(null!);
  React.useImperativeHandle(ref, () => group.current, []);
  const { subscribe, getParent } = React.useContext<Api>(context || globalContext);

  // this dependency list came from DREI's implementation
  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useLayoutEffect(() => subscribe(group), []);

  return (
    <positionMesh instance={getParent()} instanceKey={group} ref={group} {...props}>
      {children}
    </positionMesh>
  );
});

function createGeometryForInstancedMesh(count: number) {
  return new THREE.BufferGeometry()
    .setAttribute(
      'instanceMatrix',
      new THREE.InstancedBufferAttribute(new Float32Array(count * 16), 16).setUsage(THREE.DynamicDrawUsage),
    )
    .setAttribute(
      'instanceColor',
      new THREE.InstancedBufferAttribute(new Float32Array(count * 3), 3).setUsage(THREE.DynamicDrawUsage),
    );
}

/**
 * Forked from DREI, with the following changes:
 *   - ensure potentially invalid bounds aren't left behind after modifying instance matrices
 *   - remove forced `updateMatrix()` every frame to prevent duplicate calculation -- the world matrix recalculation
 *       will call `updateMatrix()` when needed. This also fixes this component ignoring `matrixAutoUpdate`.
 *   - use `updateWorldMatrix` instead of `updateMatrixWorld` so that we can avoid cascading updates down to every child
 *      of this component every single frame. That's stupid since three.js already calls `updateMatrixWorld` once per
 *      frame, and duplicating that work again during render will kill performance.
 *   - force world matrix calculation on mount to ensure it is correct for mesh and all children during the first frame
 *   - removed dead code maintaining `InstancedBufferAttribute` arrays in layout effect
 *   - added calls to `invalidate` where needed to support demand frame loop
 *   - cleaned up float array allocation
 *   - added support for growing beyond the given `initialSize` (default 1000) -- geometry will be automatically
 *       re-allocated and attributes resized in increments of `sizeIncrement` (default 500)
 *   - correctly support removing instances when children are unmounted
 *   - roughly doubled performance of instance matrix calculation by copying instead of decomposing and recomposing the
 *       world matrix
 *   - correctly keep the instanced mesh's `.count` updated so it won't waste time rendering unused instances for huge
 *       performance boost on GPU-bound machines
 */
export const Instances: ForwardRefComponent<InstancesProps, THREE.InstancedMesh> = /* @__PURE__ */ React.forwardRef<
  THREE.InstancedMesh,
  InstancesProps
>(({ context, children, range, initialSize = 1000, sizeIncrement = 500, frames = Infinity, ...props }, ref) => {
  const invalidate = useThree((state) => state.invalidate);

  const [{ localContext, instance }] = React.useState(() => {
    const localContext = React.createContext<Api>(null!);
    return {
      localContext,
      instance: React.forwardRef((props: InstanceProps, ref) => (
        <Instance context={localContext} {...props} ref={ref} />
      )),
    };
  });

  const meshRef = React.useRef<THREE.InstancedMesh>(null!);

  React.useImperativeHandle(ref, () => meshRef.current, []);

  const [count, setCount] = React.useState(initialSize);
  const [geometry, setGeometry] = React.useState(() => createGeometryForInstancedMesh(initialSize));
  const [instances, setInstances] = React.useState<React.RefObject<PositionMesh>[]>([]);

  React.useEffect(() => {
    // We have to force world matrix calculation for the instanced mesh immediately after mounting, since on the first
    //   frame we render three.js might not have calculated our world matrix yet. The call to `updateWorldMatrix` in
    //   the `useFrame` hook below does not calculate any children's world matrices for performance reasons, which is
    //    normally fine, but not on the first frame if our instanced mesh still thinks it's at Matrix4#identity()
    meshRef.current.updateWorldMatrix(false, true);
  }, []);

  let iterations = 0;

  useFrame(() => {
    if (instances.length > count) {
      geometry.dispose();

      const newCount = Math.ceil(Math.max(instances.length, initialSize) / sizeIncrement) * sizeIncrement;

      setCount(newCount);
      setGeometry(createGeometryForInstancedMesh(newCount));

      invalidate();

      return;
    }

    if (frames === Infinity || iterations < frames) {
      meshRef.current.updateWorldMatrix(false, false);
      parentMatrix.copy(meshRef.current.matrixWorld).invert();

      for (let i = 0; i < instances.length; i++) {
        const instance = instances[i].current;

        // Multiply the inverse of the InstancedMesh world matrix or else
        // Instances will be double-transformed if <Instances> isn't at identity
        instanceMatrix.copy(instance.matrixWorld).premultiply(parentMatrix);
        instanceMatrix.toArray(meshRef.current.instanceMatrix.array, i * 16);
        instance.color.toArray(meshRef.current.instanceColor!.array, i * 3);
      }

      // fumigate the rest of the unused slots to prevent ghost instances
      if (instances.length < count) {
        meshRef.current.instanceMatrix.array.fill(0, instances.length * 16);
        meshRef.current.instanceColor!.array.fill(1, instances.length * 3);
      }

      // keep `.count` updated at all times so the GPU won't chew its own tail trying to render unused instances
      meshRef.current.count = instances.length;

      meshRef.current.instanceMatrix.needsUpdate = true;
      meshRef.current.instanceColor!.needsUpdate = true;

      iterations++;
    }

    if (frames === Infinity || iterations === frames) {
      // Since we might have updated one or more instanced mesh matrices above, their existing bounds may be invalid. It
      //   is not safe to leave these behind if there's any chance that they are invalid. Incorrect bounds are very
      //   hard to debug.
      //
      // If you're here in the future because this is causing a performance issue, you probably have two options:
      //   rewrite your code so it doesn't need fresh bounds every frame, or rewrite this component so it doesn't
      //   potentially mutate matrices every frame.
      if (meshRef.current.boundingSphere) meshRef.current.boundingSphere = null;
      if (meshRef.current.boundingBox) meshRef.current.boundingBox = null;
    }

    if (frames !== Infinity && iterations < frames) {
      invalidate();
    }
  });

  const api = React.useMemo(
    () => ({
      getParent: () => meshRef,
      subscribe: (ref: any) => {
        invalidate();
        setInstances((instances) => [...instances, ref]);
        return () => {
          invalidate();
          setInstances((instances) => instances.filter((item) => item.current !== ref.current));
        };
      },
    }),
    [invalidate],
  );

  return (
    <instancedMesh
      userData={{ instances, initialSize, frames }}
      matrixAutoUpdate={false}
      ref={meshRef}
      args={[geometry, undefined, count]}
      instanceMatrix={geometry.attributes.instanceMatrix as THREE.InstancedBufferAttribute}
      instanceColor={geometry.attributes.instanceColor as THREE.InstancedBufferAttribute}
      raycast={() => null}
      {...props}
    >
      {isFunctionChild(children) ? (
        <localContext.Provider value={api}>{children(instance)}</localContext.Provider>
      ) : context ? (
        <context.Provider value={api}>{children}</context.Provider>
      ) : (
        <globalContext.Provider value={api}>{children}</globalContext.Provider>
      )}
    </instancedMesh>
  );
});

export interface MergedProps extends Omit<InstancesProps, 'children'> {
  meshes: THREE.Mesh[] | Record<string, THREE.Object3D>;
  children: (instances: any) => React.ReactNode;
}

/**
 * Forked from DREI, with the following changes:
 *   - uses our forked `Instances` component
 */
// TODO: make this non-recursive and type-safe
export const Merged: ForwardRefComponent<MergedProps, THREE.Group> = /* @__PURE__ */ React.forwardRef<
  THREE.Group,
  MergedProps
>(function Merged({ meshes, children, ...props }, ref) {
  const isArray = Array.isArray(meshes);
  // Filter out meshes from collections, which may contain non-meshes
  // @ts-expect-error Exists in drei implementation
  if (!isArray) for (const key of Object.keys(meshes)) if (!meshes[key].isMesh) delete meshes[key];

  // @ts-expect-error drei doesn't type this
  const render = (args) =>
    isArray
      ? // @ts-expect-error Exists in drei implementation
        children(...args)
      : children(
          Object.keys(meshes)
            // @ts-expect-error Exists in drei implementation
            .filter((key) => meshes[key].isMesh)
            .reduce((acc, key, i) => ({ ...acc, [key]: args[i] }), {}),
        );

  // @ts-expect-error Exists in drei implementation
  const components = (isArray ? meshes : Object.values(meshes)).map(({ geometry, material }) => (
    <Instances key={geometry.uuid} initialSize={100} geometry={geometry} material={material} {...props} />
  ));

  return <group ref={ref}>{renderRecursive(render, components)}</group>;
});

// https://github.com/jamesplease/react-composer
function renderRecursive(
  // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
  render: Function,
  // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
  components: Array<React.ReactElement<{ children: any }> | Function>,
  results: unknown[] = [],
): React.ReactElement {
  // Once components is exhausted, we can render out the results array.
  if (!components[0]) {
    return render(results);
  }

  // Continue recursion for remaining items.
  // results.concat([value]) ensures [...results, value] instead of [...results, ...value]
  // @ts-expect-error drei doesn't type this
  function nextRender(value) {
    return renderRecursive(render, components.slice(1), results.concat([value]));
  }

  // Each props.components entry is either an element or function [element factory]
  return typeof components[0] === 'function'
    ? // When it is a function, produce an element by invoking it with "render component values".
      components[0]({ results, render: nextRender })
    : // When it is an element, enhance the element's props with the render prop.
      React.cloneElement(components[0], { children: nextRender });
}

/** Idea and implementation for global instances and instanced attributes by
/*  Matias Gonzalez Fernandez https://x.com/matiNotFound
/*  and Paul Henschel https://x.com/0xca0a
*/
export function createInstances<T = InstanceProps>() {
  const context = React.createContext<Api>(null!);
  return [
    React.forwardRef<THREE.InstancedMesh, InstancesProps>((props, fref) => (
      <Instances ref={fref} context={context} {...props} />
    )),
    React.forwardRef<PositionMesh & T, T & InstanceProps>((props, fref) => (
      <Instance ref={fref} context={context} {...props} />
    )),
  ] as const;
}

export const InstancedAttribute = React.forwardRef(
  ({ name, defaultValue, normalized, usage = THREE.DynamicDrawUsage }: InstancedAttributeProps, fref) => {
    const ref = React.useRef<THREE.InstancedBufferAttribute>(null!);
    React.useImperativeHandle(fref, () => ref.current, []);
    React.useLayoutEffect(() => {
      const parent = (ref.current as any).__r3f.parent.object;
      parent.geometry.attributes[name] = ref.current;
      const value = Array.isArray(defaultValue) ? defaultValue : [defaultValue];
      const array = Array.from({ length: parent.userData.limit }, () => value).flat();
      ref.current.array = new Float32Array(array);
      ref.current.itemSize = value.length;
      // @ts-expect-error "the elites don’t want you to know this but ain't no laws in javascript let freedom ring baby"
      ref.current.count = array.length / ref.current.itemSize;
      return () => {
        delete parent.geometry.attributes[name];
      };

      // this dependency list came from DREI's implementation and I'm not mentally ready to change it yet
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [name]);
    let iterations = 0;
    useFrame(() => {
      const parent = (ref.current as any).__r3f.parent.object;
      if (parent.userData.frames === Infinity || iterations < parent.userData.frames) {
        for (let i = 0; i < parent.userData.instances.length; i++) {
          const instance = parent.userData.instances[i].current;
          const value = instance[name];
          if (value !== undefined) {
            ref.current.set(
              Array.isArray(value) ? value : typeof value.toArray === 'function' ? value.toArray() : [value],
              i * ref.current.itemSize,
            );
            ref.current.needsUpdate = true;
          }
        }
        iterations++;
      }
    });
    // @ts-expect-error we're abusing three API here by mutating immutable args
    return <instancedBufferAttribute ref={ref} usage={usage} normalized={normalized} />;
  },
);
